// Reusable modal shell for form-based CTAs (Prequalify, Trade-In, etc.).
// Same dismissal semantics as ProductDetailModal — click backdrop, ESC,
// or the close button.
//
// Children render inside a white centered card. Caller owns header,
// fields, submit button. Three states for visual feedback (idle /
// submitting / submitted) are caller-managed; this shell just provides
// the chrome.

import { useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";

interface Props {
  open: boolean;
  onClose: () => void;
  title: string;
  /** Short paragraph under the title — sets context for the form. */
  subtitle?: string;
  children: React.ReactNode;
}

export function FormModal({ open, onClose, title, subtitle, children }: Props) {
  useEffect(() => {
    if (!open) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") onClose();
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          key="backdrop"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.15 }}
          onClick={onClose}
          className="fixed inset-0 z-50 flex items-center justify-center bg-ink/60 p-4 backdrop-blur-sm"
        >
          <motion.div
            key="card"
            initial={{ opacity: 0, scale: 0.96, y: 8 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.96, y: 8 }}
            transition={{ duration: 0.2, ease: "easeOut" }}
            onClick={(e) => e.stopPropagation()}
            className="relative w-full max-w-lg overflow-hidden bg-white shadow-2xl"
          >
            <header className="border-b border-line px-6 py-4">
              <h2 className="font-display text-xl font-extrabold uppercase tracking-wide text-ink">
                {title}
              </h2>
              {subtitle && (
                <p className="mt-1 font-serif text-[14px] italic text-graphite">
                  {subtitle}
                </p>
              )}
              <button
                type="button"
                onClick={onClose}
                aria-label="Close"
                className="absolute right-3 top-3 flex h-8 w-8 items-center justify-center text-steel hover:text-ink"
              >
                <svg
                  viewBox="0 0 24 24"
                  className="h-5 w-5"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M18 6L6 18M6 6l12 12" />
                </svg>
              </button>
            </header>
            <div className="max-h-[70vh] overflow-y-auto px-6 py-5">
              {children}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

/** Standard form field row — label + control, with optional hint and
 *  per-field error message. Kept separate from FormModal so consumers
 *  can compose fields without the modal chrome too. */
export function Field({
  label,
  hint,
  error,
  children,
  required,
}: {
  label: string;
  hint?: string;
  error?: string;
  children: React.ReactNode;
  required?: boolean;
}) {
  return (
    <label className="mb-4 block">
      <span className="mb-1 block font-sans text-[14px] font-semibold text-ink">
        {label}
        {required && <span className="ml-0.5 text-red-700">*</span>}
      </span>
      {children}
      {hint && !error && (
        <span className="mt-1 block font-sans text-[14px] text-steel">{hint}</span>
      )}
      {error && (
        <span className="mt-1 block font-sans text-[14px] text-red-700">{error}</span>
      )}
    </label>
  );
}

/** Standard input styling — used by all form modals. */
export const inputClass =
  "block w-full border border-line bg-white px-3 py-2 font-sans text-[15px] text-ink focus:border-ink focus:outline-none";

/** Read-only contact summary shown at the top of form modals that
 *  already have the customer's details from the configurator's
 *  Details section. Replaces the duplicate "name / email / phone"
 *  inputs the Prequalify and Trade-In modals used to ask for.
 *  Gives the customer confidence the form knows who they are without
 *  making them re-type. */
export function ContactSummary({
  customer,
}: {
  customer: {
    firstName: string;
    lastName: string;
    email: string;
    phone: string;
  };
}) {
  const fullName = [customer.firstName, customer.lastName]
    .filter(Boolean)
    .join(" ");
  return (
    <div className="mb-4 border-l-[3px] border-amber bg-mist px-4 py-3">
      <p className="text-[12px] font-semibold uppercase tracking-tag text-steel">
        We'll contact you at
      </p>
      <dl className="mt-2 grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 font-sans text-base text-ink">
        {fullName && (
          <>
            <dt className="text-steel">Name</dt>
            <dd className="font-semibold">{fullName}</dd>
          </>
        )}
        {customer.email && (
          <>
            <dt className="text-steel">Email</dt>
            <dd>{customer.email}</dd>
          </>
        )}
        {customer.phone && (
          <>
            <dt className="text-steel">Phone</dt>
            <dd>{customer.phone}</dd>
          </>
        )}
      </dl>
    </div>
  );
}
